/* ──────────────────────────────────────────────────────────────
   src/components/Sidebar.tsx
   Drawer lateral en negro (estilo) con rutas de STAFF / ADMIN / CONSULTOR
   ────────────────────────────────────────────────────────────── */
import { useState, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterLink } from 'react-router-dom';

import {
  Drawer,
  Toolbar,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Today,
  QueryStats,
  EventBusy,
  School,
  CalendarMonth,
  People,
  MenuBook,
  LocationCity,
  ListAlt,
  AttachFile,
  ExpandLess,
  ExpandMore,
  AccessTime,
  EventNote,
  Description,
  Assignment,
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import { useIsManager } from '../hooks/useIsManager';
import { useManagerDepartments } from '../hooks/useManagerDepartments';

/* ───────── constantes visuales ───────── */
const DRAWER_W = 240; // abierto
const COLLAPSED_W = 56; // contraído

/* ───────── tipos ───────── */
export interface SidebarProps {
  collapsed?: boolean; // true → modo compacto
}

type ItemProps = {
  to: string;
  icon: JSX.Element;
  label: string;
  indent?: number;
  collapsed: boolean;
};

/* ───────── item genérico ───────── */
const Item = ({ to, icon, label, indent = 4, collapsed }: PropsWithChildren<ItemProps>) => (
  <ListItemButton
    component={RouterLink}
    to={to}
    sx={{
      pl: collapsed ? 1 : indent,
      justifyContent: collapsed ? 'center' : 'initial',
      color: 'white',
      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: collapsed ? 0 : 40,
        color: 'white',
      }}
    >
      {icon}
    </ListItemIcon>
    {!collapsed && <ListItemText primary={label} />}
  </ListItemButton>
);

/* ───────── componente principal ───────── */
export default function Sidebar({ collapsed = false }: SidebarProps) {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const { isManager } = useIsManager();
  const { departments: managerDepartments } = useManagerDepartments();

  // Helper para verificar si el usuario tiene acceso a un departamento (por rol o por ser manager)
  const hasDepartmentAccess = (department: string): boolean => {
    if (!user) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : [];
    // Verificar si tiene el rol directo (hacer cast a string para comparación)
    const hasDirectRole = userRoles.some((role) => role === department);
    // Verificar si es manager del departamento
    const isManagerOfDept = managerDepartments.includes(department as any);
    return hasDirectRole || isManagerOfDept;
  };

  /* roles */
  const isAdmin = user?.roles?.includes('admin');
  const isHr = hasDepartmentAccess('hr');
  const isConsultor = user?.roles?.includes('consultor');
  const isFacturacion = hasDepartmentAccess('facturacion');
  const isStaff =
    isAdmin ||
    isHr ||
    hasDepartmentAccess('att_familias') ||
    hasDepartmentAccess('att_profesorado') ||
    isFacturacion ||
    hasDepartmentAccess('att_alumnado') ||
    hasDepartmentAccess('admisiones');

  /* ruta correcta para la agenda */
  const agendaPath = isConsultor ? '/consultor/agenda' : '/agenda';

  /* acordeones */
  const [openProf, setOpenProf] = useState(false);
  const [openManager, setOpenManager] = useState(false);
  const [openGuardias, setOpenGuardias] = useState(false);
  const [openInformes, setOpenInformes] = useState(false);
  const [openCalendars, setOpenCalendars] = useState(false);

  /* anchura drawer */
  const width = collapsed ? COLLAPSED_W : DRAWER_W;

  const groupBtnSx = {
    pl: collapsed ? 1 : 2,
    justifyContent: collapsed ? 'center' : 'initial',
    color: 'white',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  } as const;

  const groupIconSx = {
    minWidth: collapsed ? 0 : 40,
    color: 'white',
  } as const;

  return (
    <Drawer
      variant="permanent"
      open={!collapsed}
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          overflowX: 'hidden',
          transition: (t) => t.transitions.create('width'),
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          bgcolor: '#000', // negro sólido
          color: 'white', // texto blanco
        },
      }}
    >
      <Toolbar />
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <List disablePadding>
        {/* ========== CALENDARIOS ========= */}
        {(isStaff || isConsultor) && (
          <>
            <ListItemButton onClick={() => setOpenCalendars((v) => !v)} sx={groupBtnSx}>
              <ListItemIcon sx={groupIconSx}>
                <CalendarMonth />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('menu.calendars', 'Calendarios')} />}
              {!collapsed && (openCalendars ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={openCalendars && !collapsed} timeout="auto" unmountOnExit>
              {/* AGENDA (la que ya tenías) */}
              <Item
                to={agendaPath}
                icon={<Today fontSize="small" />}
                label={t('menu.agenda')}
                indent={6}
                collapsed={collapsed}
              />

              {/* CALENDARIO GENERAL DE REUNIONES */}
              <Item
                to="/admin/manager/reuniones/calendario"
                icon={<EventNote fontSize="small" />}
                label={t('menu.reunionesCalendar', 'Reuniones')}
                indent={6}
                collapsed={collapsed}
              />
            </Collapse>

            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== INFORMES ========= */}
        {isStaff && (
          <>
            <ListItemButton onClick={() => setOpenInformes((v) => !v)} sx={groupBtnSx}>
              <ListItemIcon sx={groupIconSx}>
                <QueryStats />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('menu.reports')} />}
              {!collapsed && (openInformes ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={openInformes && !collapsed} timeout="auto" unmountOnExit>
              <Item
                to="/informes/profesores"
                icon={<People fontSize="small" />}
                label={t('menu.teachers')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/informes/clientes"
                icon={<School fontSize="small" />}
                label={t('menu.clients')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/informes/reservas"
                icon={<CalendarMonth fontSize="small" />}
                label={t('menu.bookings')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/informes/asignaturas"
                icon={<MenuBook fontSize="small" />}
                label={t('menu.subjects')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/informes/centros"
                icon={<LocationCity fontSize="small" />}
                label={t('menu.centersReports', 'Centros')}
                indent={6}
                collapsed={collapsed}
              />
            </Collapse>

            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== DISPONIBILIDAD ========= */}
        {isStaff && (
          <>
            <Item
              to="/consulta-disponibilidad"
              icon={<EventBusy />}
              label={t('menu.availability')}
              indent={2}
              collapsed={collapsed}
            />
            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== PENDIENTE REAGENDAR ========= */}
        {isStaff && (
          <>
            <Item
              to="/consulta-reservas-pendientes"
              icon={<EventBusy />}
              label={t('menu.pending')}
              indent={2}
              collapsed={collapsed}
            />
            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== FORMACIONES ========= */}
        {isStaff && (
          <>
            <Item
              to="/formaciones/nueva"
              icon={<School />}
              label={t('menu.trainings')}
              indent={2}
              collapsed={collapsed}
            />
            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== PROFESORES ========= */}
        {isStaff && (
          <>
            <ListItemButton onClick={() => setOpenProf((v) => !v)} sx={groupBtnSx}>
              <ListItemIcon sx={groupIconSx}>
                <People />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('menu.teachers')} />}
              {!collapsed && (openProf ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={openProf && !collapsed} timeout="auto" unmountOnExit>
              <Item
                to="/admin/manager/profesor-datos"
                icon={<ListAlt fontSize="small" />}
                label={t('teachers.data')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/profesor-disponibilidad"
                icon={<CalendarMonth fontSize="small" />}
                label={t('teachers.availability')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/profesor-vacaciones"
                icon={<EventBusy fontSize="small" />}
                label={t('teachers.vacations')}
                indent={6}
                collapsed={collapsed}
              />
            </Collapse>

            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}

        {/* ========== MANAGER ========= */}
        {isStaff && (
          <>
            <ListItemButton onClick={() => setOpenManager((v) => !v)} sx={groupBtnSx}>
              <ListItemIcon sx={groupIconSx}>
                <People />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('menu.manager')} />}
              {!collapsed && (openManager ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={openManager && !collapsed} timeout="auto" unmountOnExit>
              <Item
                to="/admin/manager/asignaturas"
                icon={<School fontSize="small" />}
                label={t('manager.subjects')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/centros"
                icon={<LocationCity />}
                label={t('manager.centers')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/clientes"
                icon={<People />}
                label={t('manager.clients')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/reservas"
                icon={<CalendarMonth />}
                label={t('manager.bookings', 'Reservas')}
                indent={6}
                collapsed={collapsed}
              />
              <Item
                to="/admin/manager/reuniones/reglas"
                icon={<ListAlt fontSize="small" />}
                label={t('menu.reunionRules', 'Reglas Reuniones')}
                indent={6}
                collapsed={collapsed}
              />
              <ListItemButton
                onClick={() => setOpenGuardias((v) => !v)}
                sx={{
                  pl: collapsed ? 1 : 6,
                  justifyContent: collapsed ? 'center' : 'initial',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemIcon sx={groupIconSx}>
                  <CalendarMonth fontSize="small" />
                </ListItemIcon>
                {!collapsed && <ListItemText primary={t('manager.guardias', 'Guardias')} />}
                {!collapsed && (openGuardias ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>

              <Collapse in={openGuardias && !collapsed} timeout="auto" unmountOnExit>
                <Item
                  to="/admin/manager/guardias"
                  icon={<EventBusy fontSize="small" />}
                  label={t('manager.guardias', 'Guardias')}
                  indent={6}
                  collapsed={collapsed}
                />
                <Item
                  to="/admin/manager/guardias/calendario"
                  icon={<CalendarMonth fontSize="small" />}
                  label={t('manager.guardiasCalendar', 'Guardias · Calendario')}
                  indent={6}
                  collapsed={collapsed}
                />
              </Collapse>

              {/* ===== Documentos ===== */}
              {(isAdmin || isHr || isFacturacion) && (
                <Item
                  to="/admin/manager/documentos"
                  icon={<AttachFile />}
                  label={t('manager.documents')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}
              {/* ===== Formularios ===== */}
              {isManager && (
                <Item
                  to="/admin/manager/formularios"
                  icon={<Description fontSize="small" />}
                  label={t('manager.forms', 'Formularios')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}
              {/* ===== Evaluaciones ===== */}
              {isManager && (
                <Item
                  to="/admin/manager/evaluaciones"
                  icon={<Assignment fontSize="small" />}
                  label={t('manager.evaluations', 'Evaluaciones')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}
              {isAdmin && (
                <Item
                  to="/admin/manager/festivos"
                  icon={<EventBusy />}
                  label={t('manager.holidays')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}
              <Item
                to="/admin/manager/staff-disponibilidad"
                icon={<AccessTime fontSize="small" />}
                label={t('manager.staffAvailability', 'Disponibilidad Staff')}
                indent={6}
                collapsed={collapsed}
              />

              {(isAdmin || isHr) && (
                <Item
                  to="/admin/manager/usuarios"
                  icon={<People />}
                  label={t('manager.users')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}

              {/* ===== Auditoría (solo admin) ===== */}
              {isAdmin && (
                <Item
                  to="/admin/manager/audit-logs"
                  icon={<Description fontSize="small" />}
                  label={t('manager.auditLogs', 'Auditoría')}
                  indent={6}
                  collapsed={collapsed}
                />
              )}
            </Collapse>

            {!collapsed && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          </>
        )}
      </List>
    </Drawer>
  );
}
